/**
 * Revisions are a tool script supervisors use to mark notable points in a
 * script's development. It keeps track of explicit, named snapshots in the
 * version history of the script and helps people answer the questions:
 * - Which pages in this script have changed?
 * - Which lines have been added or deleted?
 * - Are these changes part of the current revision or a previous one?
 *
 * This plugin keeps track of some essential state:
 * - What are the display attributes associated with a revision (a "revision set")?
 *   - Text and page color
 *   - The text mark used to indicate what line corresponds to that revision
 * - Whether the revision indicates a full draft (a convention for when a
 *   majority of the script has been edited)
 * - What is the active revision and what snapshot is associated with it?
 * - What are the previously selected revisions, in what order, and what are the
 *   snapshots associated with them?
 * - What potential revision sets are available to the user to select from (and how
 *   can they add to the available options)?
 * - What are the preferences on what to show in the editor and in print?
 *
 * This essential state is a part of the plugin's state but is built from the
 * document's `attr.revisions` field for persistence. We also keep track of some
 * states necessary for the editor to function; this state will remain part of
 * the plugin:
 *
 * - The changes between the current document and the snapshot associated with
 *   the start of the active revision set: this is implemented as a
 *   prosemirror-changeset object that is incrementally updated as people type
 *   in the document
 * - A DecorationSet to render inline decorations for changed lines
 * - Widget decorations to mark asterisks for changed lines
 * - A `RevisionTracker` object that provides info about what revision is
 *   associated with a particular selection in the document. This is used to
 *   determine what page a revision belongs to by examing the revision based on
 *   that page's start and end position by the inlinePagination plugin
 */

import { type ClientInferResponseBody } from '@ts-rest/core'
import { Change, ChangeSet } from 'prosemirror-changeset'
import {
  closeHistory,
  history,
  isHistoryTransaction,
} from 'prosemirror-history'
import { keydownHandler } from 'prosemirror-keymap'
import { Node } from 'prosemirror-model'
import {
  Command,
  EditorState,
  Plugin,
  PluginKey,
  PluginView,
  Transaction,
} from 'prosemirror-state'
import { Step } from 'prosemirror-transform'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'
import invariant from 'tiny-invariant'
import { z } from 'zod'

import { RevisionMarkSet, RevisionStep, type Span } from '@showrunner/codex'
import { schema, constants } from '@showrunner/prose-schemas'
import { scrapi } from '@showrunner/scrapi'

import { ScrapiClient } from '#util/buildScrapiClient'
import { DatadogClient } from '#util/datadog/DatadogClient'
import { extractTsRestSuccess } from '#util/extractTsRest'
import { getPageLayout } from '#util/PageLayout'
import { getBlockMargins, Line } from '#util/pagination/helpers'
import type { Unit } from '#util/Unit'

import type { RevisionLoader } from '../contracts/capabilities'

import { getConfigData } from './configData'
import { getPagination } from './pagination'
import type { ScriptPagination } from './pagination/paginationPlugin'
import {
  buildGutterDecorations,
  GutterBlockMark,
} from './revision-asterisks/helpers'
import {
  type DeletionChange,
  DeletionSet,
  type OwnedDeletionChange,
} from './revision-deletions'
import { isInTitlePage } from './titlePageView'

import styles from './revisions.module.css'

const { NodeGroupMap } = constants

const ddLog = DatadogClient.getInstance()

export interface RevisionSet {
  label: string
  isFullDraft: boolean
  mark: string
  textColor: string
  pageColor: string
}

export interface RevisionSnapshot {
  id: string
  version: number
}

export interface Revision extends RevisionSet {
  snapshot: RevisionSnapshot
  toggleSnapshots?: RevisionSnapshot[]
}

export function revisionId(revision: Revision): string {
  return revision.snapshot.id
}

function revisionVersion(revision: Revision): number {
  return revision.snapshot.version
}

export function revisionStepData(
  revision: Revision,
  stepCount: number,
): (string | null)[] {
  const snapshots = [revision.snapshot, ...(revision.toggleSnapshots ?? [])]
  const firstSnapshot = revision.snapshot
  const data: (string | null)[] = []
  let boundaryIndex = 0
  let activeRevisionId: string | null = null

  for (let index = 0; index < stepCount; index++) {
    const stepVersion = firstSnapshot.version + index + 1
    while (
      boundaryIndex < snapshots.length &&
      stepVersion > snapshots[boundaryIndex].version
    ) {
      activeRevisionId = boundaryIndex % 2 === 0 ? firstSnapshot.id : null
      boundaryIndex++
    }
    data.push(activeRevisionId)
  }

  return data
}

export const defaultRevisionSets: RevisionSet[] = [
  {
    label: 'Blue Rev.',
    isFullDraft: false,
    mark: '*',
    textColor: '#000000',
    pageColor: '#a5d8ff',
  },
  {
    label: 'Pink Rev.',
    isFullDraft: false,
    mark: '*',
    textColor: '#000000',
    pageColor: '#fcc2d7',
  },
  {
    label: 'Yellow Rev.',
    isFullDraft: false,
    mark: '*',
    textColor: '#000000',
    pageColor: '#FFF3BF',
  },
  {
    label: 'Green Rev.',
    isFullDraft: false,
    mark: '*',
    textColor: '#000000',
    pageColor: '#b2f2bb',
  },
  {
    label: 'Goldenrod Rev.',
    isFullDraft: false,
    mark: '*',
    textColor: '#000000',
    pageColor: '#f3e3bb',
  },
  {
    label: 'Buff Rev.',
    isFullDraft: false,
    mark: '*',
    textColor: '#000000',
    pageColor: '#ffd6bd',
  },
  {
    label: 'Salmon Rev.',
    isFullDraft: false,
    mark: '*',
    textColor: '#000000',
    pageColor: '#ffbeb7',
  },
  {
    label: 'Cherry Rev.',
    isFullDraft: false,
    mark: '*',
    textColor: '#000000',
    pageColor: '#ff99a5',
  },
  {
    label: 'Tan Rev.',
    isFullDraft: false,
    mark: '*',
    textColor: '#000000',
    pageColor: '#ffe6c4',
  },
  {
    label: 'Ivory Rev.',
    isFullDraft: false,
    mark: '*',
    textColor: '#000000',
    pageColor: '#f0ecd9',
  },
]

// Options for showing revision marks(text) in the page header/footer
export const showRevisionMarkOptions: {
  value: RevisionMarkOption
  label: string
}[] = [
  { value: 'collated', label: 'Collated script' },
  { value: 'active', label: 'From active revision set only' },
  { value: 'all', label: 'From all revision sets' },
  {
    value: 'all-after-full-draft',
    label: 'From all revision sets after and including latest full draft',
  },
  { value: 'none', label: 'None' },
]

const RevisionMarkOptionSchema = z.enum([
  'collated',
  'active',
  'all',
  'all-after-full-draft',
  'none',
])

export type RevisionMarkOption = z.infer<typeof RevisionMarkOptionSchema>

const RevisionSetSchema = z.object({
  label: z.string(),
  isFullDraft: z.boolean(),
  mark: z.string(),
  textColor: z.string(),
  pageColor: z.string(),
})

// This is the current "revision shape" saved on the document attribute
const RevisionsJsonSchema = scrapi.scripts.updateRevisions.responses[200]

// These scripts were saved before snapshots were introduced
const LegacyRevisionsJsonSchema = z.object({
  revisions: z.object({
    previous: z.array(RevisionSetSchema),
    active: RevisionSetSchema,
    available: z.array(RevisionSetSchema),
  }),
  enabled: z.boolean(),
  showPageColors: z.boolean(),
  markOption: RevisionMarkOptionSchema,
})

type RevisionsDocumentAttrs = {
  revisions?:
    | z.infer<typeof RevisionsJsonSchema>
    | z.infer<typeof LegacyRevisionsJsonSchema>
    | null
}

export class RevisionsState {
  constructor(
    readonly revisions: RevisionSelectList,
    readonly enabled: boolean,
    readonly showPageColors: boolean,
    readonly markOption: RevisionMarkOption,
  ) {}

  static fromDefaults() {
    const [first, ...rest] = defaultRevisionSets
    // Create a placeholder snapshot for the default active revision
    const defaultActiveRevision: Revision = {
      ...first,
      snapshot: { id: 'default', version: 0 },
    }
    return new RevisionsState(
      new RevisionSelectList([], defaultActiveRevision, rest),
      false,
      true,
      'active',
    )
  }

  static fromJSON(json: {
    revisions: {
      previous: Revision[]
      active: Revision
      available: RevisionSet[]
    }
    enabled: boolean
    showPageColors: boolean
    markOption: RevisionMarkOption
  }) {
    return new RevisionsState(
      RevisionSelectList.fromJSON(json.revisions),
      json.enabled,
      json.showPageColors,
      json.markOption,
    )
  }

  withShowPageColors(showPageColors: boolean): RevisionsState {
    return new RevisionsState(
      this.revisions,
      this.enabled,
      showPageColors,
      this.markOption,
    )
  }

  // Flattened list of all revisions with snapshots (previous + active)
  get docRevisions() {
    const { previous, active } = this.revisions
    return [previous, active].flat()
  }

  get visibleRevisions() {
    const { active } = this.revisions

    switch (this.markOption) {
      case 'none':
        return []
      case 'active':
        return [active]
      case 'collated':
      case 'all':
        return this.docRevisions
      case 'all-after-full-draft': {
        const index = this.docRevisions.findLastIndex((r) => r.isFullDraft)
        return this.docRevisions.slice(Math.max(index, 0))
      }
    }
  }

  get revisionMap() {
    return new Map(this.docRevisions.map((rev) => [revisionId(rev), rev]))
  }

  /**
   * @returns the revision spans visible under the selected mark option
   */
  visibleChanges({
    tracker,
    pagination,
    doc,
  }: {
    tracker: RevisionSource
    pagination: ScriptPagination | null
    doc: Node | null
  }): VisibleRevisionChanges {
    if (this.markOption === 'none') return VisibleRevisionChanges.empty

    const revisionMap = this.revisionMap
    const spans = tracker.allChanges()
    const ownedDeletions =
      doc && pagination ? tracker.deletions.ownedBy(doc, pagination) : []

    if (this.markOption === 'collated' && pagination) {
      return VisibleRevisionChanges.collated({
        spans,
        ownedDeletions,
        pages: pagination.pages,
        revisionMap,
      })
    }

    switch (this.markOption) {
      case 'active': {
        const activeRevisionId = revisionId(this.revisions.active)
        return new VisibleRevisionChanges(
          spans.filter((span) => span.revision === activeRevisionId),
          ownedDeletions.filter(
            ({ change }) => change.revision === activeRevisionId,
          ),
          revisionMap,
        )
      }
      case 'collated':
      case 'all':
      case 'all-after-full-draft':
        return new VisibleRevisionChanges(spans, ownedDeletions, revisionMap)
    }
  }

  toJSON() {
    return {
      revisions: {
        previous: this.revisions.previous,
        active: this.revisions.active,
        available: this.revisions.available,
      },
      enabled: this.enabled,
      showPageColors: this.showPageColors,
      markOption: this.markOption,
    }
  }

  /**
   * @returns true if `previous` currently diffs from the same full-draft
   * cutoff as `other`. The mark option only moves the cutoff when it's
   * 'all-after-full-draft', in which case it's whichever previous revision
   * was most recently marked as a full draft. Otherwise it's always the
   * oldest completed revision, or the active revision when none have been
   * completed yet
   */
  hasSameFullDraftCutoffAs(other: RevisionsState): boolean {
    const cutoff =
      this.markOption === 'all-after-full-draft'
        ? this.revisions.fullDraftCutoff
        : this.revisions.firstRevision
    const otherCutoff =
      other.markOption === 'all-after-full-draft'
        ? other.revisions.fullDraftCutoff
        : other.revisions.firstRevision

    return cutoff.snapshot.id === otherCutoff.snapshot.id
  }
}

export type RevisionsPluginState =
  | { tag: 'Inactive' }
  | {
      tag: 'Loading'
      revisionsState: RevisionsState
    }
  | {
      tag: 'Resolved'
      revisionsState: RevisionsState
      tracker: RevisionTracker
    }
  | { tag: 'Error'; reason: string }

export const revisionsKey = new PluginKey<RevisionsPluginState>('revisions')

// Use a single reference for this value to preserve immutability in callers
const INACTIVE_REVISIONS: RevisionsPluginState = { tag: 'Inactive' }

interface RevisionData {
  firstSnapshotDoc: Node
  steps: Step[]
  changes: Change[]
  marks: Span[]
}

type ActiveRevisionSnapshotData = ClientInferResponseBody<
  typeof scrapi.scripts.revisions.get,
  200
>

// resolves null when there is no revision data to load (e.g. a snapshot
// whose doc predates revisions)
export type ActiveRevisionSnapshotLoader =
  () => Promise<ActiveRevisionSnapshotData | null>

export const fetchActiveRevisionSnapshot = async (
  scrapiClient: ScrapiClient,
  scriptId: string,
): Promise<ActiveRevisionSnapshotData> => {
  const request = scrapiClient.scripts.revisions.get({
    params: { scriptId },
  })
  const { body } = await extractTsRestSuccess(request, 200)
  return body
}

export interface RevisionsPluginConfig {
  // resolves null when there is no revision data to load (e.g. a snapshot
  // whose doc predates revisions)
  loadActiveRevisionSnapshot: ActiveRevisionSnapshotLoader
  updateAfterCreation?: boolean
}

export const createRevisionsConfig = (
  revisions?: RevisionLoader,
): RevisionsPluginConfig => ({
  loadActiveRevisionSnapshot: revisions?.load ?? (() => Promise.resolve(null)),
})

export const revisionsPlugin = (
  config: RevisionsPluginConfig,
): Plugin<RevisionsPluginState> => {
  return new Plugin<RevisionsPluginState>({
    key: revisionsKey,
    state: {
      init(_, state) {
        const result = revisionsStateFromDocument(state.doc)
        switch (result.tag) {
          case 'None':
            return INACTIVE_REVISIONS
          case 'Invalid':
            return {
              tag: 'Error',
              reason: 'Invalid revisions data in document',
            }
          case 'Valid':
            return { tag: 'Loading', revisionsState: result.revisionsState }
        }
      },
      apply(tr, state, oldState, newState) {
        if (state.tag === 'Error') {
          return state
        }

        const versionData = tr.getMeta(revisionsKey) as RevisionData | undefined

        if (state.tag === 'Loading' && versionData) {
          const {
            firstSnapshotDoc,
            steps,
            changes,
            marks: revisionMarks,
          } = versionData

          const changeSetBetweenSnapshots = ChangeSet.create(
            firstSnapshotDoc,
            undefined,
            undefined,
            changes,
          )

          // then add steps to the changeset to get the final changeset
          const changeSet = changeSetBetweenSnapshots.addSteps(
            newState.doc,
            steps.map((s) => s.getMap()),
            revisionStepData(
              state.revisionsState.revisions.active,
              steps.length,
            ),
          )

          const marks = RevisionMarkSet.fromJSON(revisionMarks).addSteps(steps)

          const tracker = new RevisionTracker(
            firstSnapshotDoc,
            changeSet,
            marks,
            state.revisionsState.revisions.active,
          )

          return {
            tag: 'Resolved',
            revisionsState: state.revisionsState,
            tracker,
          }
        }

        if (oldState.doc.attrs.revisions !== newState.doc.attrs.revisions) {
          const result = revisionsStateFromDocument(newState.doc)
          if (result.tag === 'Invalid') {
            return {
              tag: 'Error',
              reason: 'Invalid revisions data in document',
            }
          }

          if (state.tag === 'Inactive') {
            if (result.tag === 'Valid') {
              return {
                tag: 'Loading',
                revisionsState: result.revisionsState,
              }
            }
            return state
          }

          if (result.tag === 'None') {
            return INACTIVE_REVISIONS
          }

          const newRevisionsState = result.revisionsState

          const revisionsAreBeingEnabled =
            !state.revisionsState.enabled && newRevisionsState.enabled

          if (revisionsAreBeingEnabled) {
            return { tag: 'Loading', revisionsState: newRevisionsState }
          }

          if (!newRevisionsState.enabled) {
            return state.tag === 'Resolved'
              ? {
                  tag: 'Resolved',
                  revisionsState: newRevisionsState,
                  tracker: state.tracker,
                }
              : { tag: 'Loading', revisionsState: newRevisionsState }
          }

          // A previously-loaded snapshot no longer reflects what should be
          // displayed if the full-draft cutoff itself moved -- reload rather
          // than reusing the current tracker
          if (
            !state.revisionsState.hasSameFullDraftCutoffAs(newRevisionsState)
          ) {
            return { tag: 'Loading', revisionsState: newRevisionsState }
          }

          const activeRevision = newRevisionsState.revisions.active

          const activeRevisionChanged =
            revisionId(state.revisionsState.revisions.active) !==
            revisionId(newRevisionsState.revisions.active)

          const tracker =
            activeRevisionChanged && state.tag === 'Resolved'
              ? state.tracker.activateRevision(newState.doc, activeRevision)
              : state.tag === 'Resolved'
                ? state.tracker
                : null

          if (tracker) {
            return {
              tag: 'Resolved',
              revisionsState: newRevisionsState,
              tracker,
            }
          }

          return { tag: 'Loading', revisionsState: newRevisionsState }
        }

        const hasRevisionStep = tr.steps.some((s) => s instanceof RevisionStep)
        if (state.tag === 'Resolved' && (tr.docChanged || hasRevisionStep)) {
          const tracker = state.revisionsState.enabled
            ? state.tracker.apply(tr, newState.doc)
            : state.tracker.applyUntracked(tr, newState.doc)
          return {
            tag: 'Resolved',
            revisionsState: state.revisionsState,
            tracker,
          }
        }

        return state
      },
    },
    appendTransaction(_transactions, oldState, newState) {
      if (revisionBoundaryChanged(oldState.doc.attrs, newState.doc.attrs)) {
        return resetHistory(newState)
      }
    },
    props: {
      decorations(state) {
        const pluginState = this.getState(state)
        if (pluginState?.tag !== 'Resolved') return null
        return buildInsertionDecorations(
          state,
          pluginState.tracker,
          pluginState.revisionsState,
        )
      },
      handleKeyDown: keydownHandler({
        'Mod-Alt-]': includeInRevision,
        'Mod-Alt-[': excludeFromRevision,
      }),
    },
    view(editorView: EditorView) {
      return new RevisionsPluginView(editorView, config)
    },
  })
}

const historyPlugin = history()
function resetHistory(state: EditorState, tr = state.tr) {
  const historyStateField = historyPlugin.spec.state
  invariant(historyStateField, 'History plugin requires a state field')

  const historyState = historyStateField.init({ doc: state.doc }, state)
  return tr.setMeta(historyPlugin, { historyState })
}

function revisionBoundaryChanged(
  oldAttrs: RevisionsDocumentAttrs,
  newAttrs: RevisionsDocumentAttrs,
): boolean {
  const oldBoundary = revisionBoundaryKey(oldAttrs)
  const newBoundary = revisionBoundaryKey(newAttrs)

  return oldBoundary !== newBoundary
}

function revisionBoundaryKey(attrs: RevisionsDocumentAttrs): string | null {
  const revisions = attrs.revisions
  if (!revisions || typeof revisions.enabled !== 'boolean') {
    return null
  }

  // Legacy revision attrs predate snapshots. Keep their boundary stable until
  // they are migrated to the current Scrapi shape.
  const activeSnapshotId =
    'active' in revisions && revisions.active && 'snapshot' in revisions.active
      ? revisions.active.snapshot.id
      : ''

  return `${revisions.enabled}:${activeSnapshotId}`
}

/**
 * This is a bit of a weird pattern, but: we can create a view for the plugin
 * that runs once the state has been initialized and the editor is rendered for
 * the first time. We use this to kick off loading the active revision snapshot
 */
class RevisionsPluginView implements PluginView {
  private requestedRevisionsState: RevisionsState | undefined

  constructor(
    readonly editorView: EditorView,
    readonly config: RevisionsPluginConfig,
  ) {
    if (config.updateAfterCreation) this.update(editorView)
  }

  update(editorView: EditorView, previousEditorState?: EditorState) {
    const pluginState = revisionsKey.getState(editorView.state)
    if (!pluginState) return
    if (pluginState.tag !== 'Loading') return

    const previousPluginState = previousEditorState
      ? revisionsKey.getState(previousEditorState)
      : undefined
    const enteredLoading = previousPluginState?.tag !== 'Loading'
    const loadingScopeChanged =
      previousPluginState?.tag === 'Loading' &&
      !this.hasSameLoadScope(
        previousPluginState.revisionsState,
        pluginState.revisionsState,
      )

    if (
      this.requestedRevisionsState === undefined ||
      enteredLoading ||
      loadingScopeChanged
    ) {
      this.loadActiveSnapshot()
    }
  }

  destroy() {}

  // The active revision snapshot loaded from the server depends on the
  // full-draft cutoff (see hasSameFullDraftCutoffAs) and on the active
  // revision (it controls the snapshot version steps are diffed from). If
  // either differs, a snapshot loaded for "a" doesn't reflect what should be
  // displayed for "b"
  private hasSameLoadScope(a: RevisionsState, b: RevisionsState): boolean {
    return (
      a.hasSameFullDraftCutoffAs(b) &&
      a.revisions.active.snapshot.id === b.revisions.active.snapshot.id
    )
  }

  private loadActiveSnapshot() {
    const pluginState = revisionsKey.getState(this.editorView.state)
    if (!pluginState) return
    if (pluginState.tag !== 'Loading') return

    const requestedRevisionsState = pluginState.revisionsState
    this.requestedRevisionsState = requestedRevisionsState
    const parseStep = (step: unknown) => Step.fromJSON(schema, step)

    this.config
      .loadActiveRevisionSnapshot()
      .then((revisionData) => {
        if (!revisionData) return
        if (this.editorView.isDestroyed) return
        const currentPluginState = revisionsKey.getState(this.editorView.state)
        if (currentPluginState?.tag !== 'Loading') return
        if (
          !this.hasSameLoadScope(
            currentPluginState.revisionsState,
            requestedRevisionsState,
          )
        ) {
          return
        }
        const { initialDoc, changes, steps, marks } = revisionData
        const { tr } = this.editorView.state

        tr.setMeta(revisionsKey, {
          firstSnapshotDoc: schema.nodeFromJSON(initialDoc),
          changes: changes.map((change) => Change.fromJSON(change)),
          steps: steps.map(parseStep),
          marks: marks,
        })

        tr.setMeta('choo', true) // Required to force a re-render for inline page breaks

        this.editorView.dispatch(tr)
      })
      .catch((error) => {
        ddLog.warn('Failed to load active revision snapshot', { error })
      })
  }
}

interface PrintRevisionsConfig {
  tracker: RevisionTracker
  showPageColors: boolean
}

/**
 * Create a revisions plugin for printing.
 */
export function revisionsPluginForPrinting(
  config: PrintRevisionsConfig,
): Plugin<RevisionsPluginState> {
  const { tracker, showPageColors } = config

  return new Plugin<RevisionsPluginState>({
    key: revisionsKey,
    state: {
      init(_, state) {
        const result = revisionsStateFromDocument(state.doc)
        invariant(
          result.tag === 'Valid',
          'Print plugin requires valid revisions data',
        )

        return {
          tag: 'Resolved',
          revisionsState:
            result.revisionsState.withShowPageColors(showPageColors),
          tracker,
        }
      },
      apply(tr, state, _oldState, newState) {
        if (state.tag !== 'Resolved') return state
        return {
          tag: 'Resolved',
          revisionsState: state.revisionsState,
          tracker: state.tracker.applyUntracked(tr, newState.doc),
        }
      },
    },
    props: {
      decorations(state) {
        const pluginState = this.getState(state)
        if (pluginState?.tag !== 'Resolved') return null
        return buildInsertionDecorations(
          state,
          pluginState.tracker,
          pluginState.revisionsState,
        )
      },
    },
  })
}

type RevisionParseResult =
  | { tag: 'None' }
  | { tag: 'Valid'; revisionsState: RevisionsState }
  | { tag: 'Invalid' }

/**
 * @returns the revisions state on the document attribute, if it exists
 */
export function revisionsStateFromDocument(doc: Node): RevisionParseResult {
  const docRevisions = doc.attrs.revisions

  if (!docRevisions) {
    return { tag: 'None' }
  }

  const legacyParseResult = LegacyRevisionsJsonSchema.safeParse(docRevisions)

  if (legacyParseResult.success) {
    ddLog.warn('Script visited with revisions lacking snapshot data')
    const { revisions, ...rest } = legacyParseResult.data

    // @TODO: better to just start failing on these? Marking revision ids as the
    // snapshot id was pretty harmless, but making up the snapshot id just so be
    // able to "re-use" an old snapshot seems like a bad call
    const previous: Revision[] = revisions.previous.map(
      (revisionSet, index) => ({
        ...revisionSet,
        snapshot: { id: `legacy-previous-${index}`, version: 0 },
      }),
    )

    const active: Revision = {
      ...revisions.active,
      snapshot: { id: 'legacy-active', version: 0 },
    }

    return {
      tag: 'Valid',
      revisionsState: RevisionsState.fromJSON({
        ...rest,
        revisions: {
          previous,
          active,
          available: revisions.available,
        },
      }),
    }
  }

  // Try parsing as current data (with snapshots)
  const parseResult = RevisionsJsonSchema.safeParse(docRevisions)

  if (!parseResult.success) {
    ddLog.error('Invalid revisions data in document', {
      error: parseResult.error,
    })
    return { tag: 'Invalid' }
  }

  const { previous, active, available, ...rest } = parseResult.data

  // Use the API data directly - the shape matches our types
  return {
    tag: 'Valid',
    revisionsState: RevisionsState.fromJSON({
      ...rest,
      revisions: {
        previous,
        active,
        available,
      },
    }),
  }
}

function revisionSetsEqual(a: RevisionSet, b: RevisionSet): boolean {
  return (
    a.label === b.label &&
    a.isFullDraft === b.isFullDraft &&
    a.mark === b.mark &&
    a.textColor === b.textColor &&
    a.pageColor === b.pageColor
  )
}

function revisionsEqual(a: Revision, b: Revision): boolean {
  return revisionId(a) === revisionId(b)
}

/**
 * An immutable data structure that represents a list of revisions with exactly one active selection.
 *
 * The structure maintains three categories in a SelectList/Zipper pattern:
 * - previous: revisions that have been used before the active one
 * - active: the currently selected revision
 * - available: revision sets that can be selected in the future
 */
export class RevisionSelectList {
  constructor(
    public readonly previous: readonly Revision[],
    public readonly active: Revision,
    public readonly available: readonly RevisionSet[],
  ) {}

  static fromJSON(json: {
    previous: Revision[]
    active: Revision
    available: RevisionSet[]
  }) {
    return new RevisionSelectList(json.previous, json.active, json.available)
  }

  get firstRevision(): Revision {
    return this.previous[0] ?? this.active
  }

  get mostRecentFullDraft(): Revision | undefined {
    if (this.active.isFullDraft) return this.active

    return this.previous.findLast((revision) => revision.isFullDraft)
  }

  get fullDraftCutoff(): Revision {
    return this.mostRecentFullDraft ?? this.firstRevision
  }

  /**
   * @returns true if all revisions are deeply equal
   */
  eq(other: RevisionSelectList): boolean {
    if (this === other) {
      return true
    }

    if (!revisionsEqual(this.active, other.active)) {
      return false
    }

    if (this.previous.length !== other.previous.length) {
      return false
    }

    for (let i = 0; i < this.previous.length; i++) {
      if (!revisionsEqual(this.previous[i], other.previous[i])) {
        return false
      }
    }

    if (this.available.length !== other.available.length) {
      return false
    }

    for (let i = 0; i < this.available.length; i++) {
      if (!revisionSetsEqual(this.available[i], other.available[i])) {
        return false
      }
    }

    return true
  }
}

// RevisionTracker tracks which nodes are updated and where
export class RevisionTracker {
  constructor(
    // snapshot of the document at the start of the active revision
    public readonly documentSnapshot: Node,
    // changeset between the initial snapshot and the current document
    public readonly changeSet: ChangeSet,
    // custom layer of revision marks
    public readonly marks: RevisionMarkSet,
    // current active revision set
    public readonly activeRevision: Revision,
    // immutable deletion view derived from the changeset
    public readonly deletions: DeletionSet = DeletionSet.from(changeSet),
  ) {}

  /**
   * Create a RevisionTracker starting from the current document state.
   */
  static startTracking(
    doc: Node,
    revision: Revision,
    marks: RevisionMarkSet = RevisionMarkSet.create(),
  ): RevisionTracker {
    const snapshot = doc
    const changeSet = ChangeSet.create(snapshot)
    return new RevisionTracker(snapshot, changeSet, marks, revision)
  }

  apply(tr: Transaction, newDoc: Node): RevisionTracker {
    const hasRevisionStep = tr.steps.some((s) => s instanceof RevisionStep)

    if (!tr.docChanged && !hasRevisionStep) {
      return this
    }

    // Apply all steps to update mark positions and revision assignments.
    // For undo/redo, this remaps positions through the inverted steps and
    // applies any inverted RevisionSteps (restoring the previous marks).
    let marks = RevisionMarkSet.fromJSON(this.marks.toJSON()).applyTransaction(
      tr,
    )

    // For undo/redo of doc changes, recompute the diff from scratch
    if (isHistoryTransaction(tr) && tr.docChanged) {
      marks = restoreDeletedRevisionMarksOnUndo(marks, tr)
      const updatedChangeSet = this.changeSet.addSteps(
        newDoc,
        tr.mapping.maps,
        this.activeRevision.snapshot.id,
      )

      return new RevisionTracker(
        this.documentSnapshot,
        updatedChangeSet,
        marks,
        this.activeRevision,
      )
    }

    if (!tr.docChanged) {
      // Only RevisionStep(s) — changeSet is unchanged
      return new RevisionTracker(
        this.documentSnapshot,
        this.changeSet,
        marks,
        this.activeRevision,
        this.deletions,
      )
    }

    // Normal doc-changing transaction
    const updatedChangeSet = this.changeSet.addSteps(
      newDoc,
      tr.mapping.maps,
      revisionId(this.activeRevision),
    )

    return new RevisionTracker(
      this.documentSnapshot,
      updatedChangeSet,
      marks,
      this.activeRevision,
    )
  }

  applyUntracked(tr: Transaction, newDoc: Node): RevisionTracker {
    const hasRevisionStep = tr.steps.some((s) => s instanceof RevisionStep)

    if (!tr.docChanged && !hasRevisionStep) {
      return this
    }

    const marks = this.marks.applyTransaction(tr)
    if (!tr.docChanged) {
      return new RevisionTracker(
        this.documentSnapshot,
        this.changeSet,
        marks,
        this.activeRevision,
        this.deletions,
      )
    }

    const updatedChangeSet = this.changeSet.addSteps(
      newDoc,
      tr.mapping.maps,
      tr.mapping.maps.map(() => null),
    )

    return new RevisionTracker(
      this.documentSnapshot,
      updatedChangeSet,
      marks,
      this.activeRevision,
    )
  }

  // activate a new revision
  activateRevision(doc: Node, revision: Revision) {
    return new RevisionTracker(
      doc,
      this.changeSet,
      this.marks,
      revision,
      this.deletions,
    )
  }

  get changes() {
    return this.changeSet.changes
  }

  // get the latest revision associated with any change overlapping with the given range
  latestRevision(
    from: number,
    to: number,
    revisions: ReadonlyMap<string, Revision>,
  ): Revision | null {
    return latestRevision(
      [...this.changesBetween(from, to), ...this.deletions.between(from, to)],
      revisions,
    )
  }

  // get all change spans
  allChanges(): Span[] {
    return this.changesBetween(0, Infinity)
  }

  // get all insertion and revision-mark spans overlapping with the given range
  changesBetween(from: number, to: number): Span[] {
    const insertions = this.insertionSpansBetween(from, to)
    const marks = this.marks.changesBetween(from, to)
    return RevisionMarkSet.merge(insertions, marks, from, to)
  }

  // get inserted spans from the changeset that overlap with the given range
  private insertionSpansBetween(from: number, to: number): Span[] {
    const spans: Span[] = []
    for (const change of this.changeSet.changes) {
      // check for overlap with the inserted range
      if (change.fromB < to && change.toB > from) {
        let start = 0
        for (const span of change.inserted) {
          const spanStart = change.fromB + start
          const spanEnd = spanStart + span.length
          if (spanEnd > from && spanStart < to) {
            spans.push({
              from: Math.max(spanStart, from),
              to: Math.min(spanEnd, to),
              revision: span.data,
            })
          }
          start += span.length
        }
      }
    }

    return spans
  }

  // check for overlapping range of change
  changesWithin(from: number, to: number): boolean {
    return [
      ...this.changesBetween(from, to),
      ...this.deletions.between(from, to),
    ].some((change) => change.revision !== null)
  }
}

function restoreDeletedRevisionMarksOnUndo(
  marks: RevisionMarkSet,
  tr: Transaction,
): RevisionMarkSet {
  // Restore revision color across content reinserted by undo.
  const historyMeta = tr.getMeta('history$') as { redo?: boolean } | undefined
  if (!historyMeta || historyMeta.redo) return marks

  let spans = marks.spans
  for (const map of tr.mapping.maps) {
    map.forEach((oldStart, oldEnd, newStart, newEnd) => {
      if (oldStart !== oldEnd || newStart >= newEnd) return

      spans = mergeRevisionMarkGap(spans, newStart, newEnd)
    })
  }

  return new RevisionMarkSet(spans)
}

function mergeRevisionMarkGap(spans: Span[], from: number, to: number): Span[] {
  const rightIndex = spans.findIndex((span) => span.from === to)
  if (rightIndex <= 0) return spans

  const left = spans[rightIndex - 1]
  const right = spans[rightIndex]
  if (left.to !== from || left.revision !== right.revision) return spans

  return [
    ...spans.slice(0, rightIndex - 1),
    { ...left, to: right.to },
    ...spans.slice(rightIndex + 1),
  ]
}

interface RevisionSource {
  allChanges(): Span[]
  readonly deletions: DeletionSet
}

class VisibleRevisionChanges {
  static readonly empty = new VisibleRevisionChanges([], [], new Map())

  static collated({
    spans,
    ownedDeletions,
    revisionMap,
    pages,
  }: {
    spans: readonly Span[]
    ownedDeletions: readonly OwnedDeletionChange[]
    revisionMap: ReadonlyMap<string, Revision>
    pages: { start: number; end: number; lines: Line[] }[]
  }): VisibleRevisionChanges {
    const visibleSpans: Span[] = []
    const visibleDeletions: OwnedDeletionChange[] = []

    for (const page of pages) {
      const pageSpans = spans.filter(
        (span) => span.from < page.end && span.to > page.start,
      )
      const pageLines = new Set(page.lines)
      const pageDeletions = ownedDeletions.filter(({ line }) =>
        pageLines.has(line),
      )
      const pageRevision = latestRevision(
        [...pageSpans, ...pageDeletions.map(({ change }) => change)],
        revisionMap,
      )
      if (!pageRevision) continue

      const pageRevisionId = revisionId(pageRevision)
      for (const span of pageSpans) {
        if (span.revision === pageRevisionId) {
          visibleSpans.push(span)
        }
      }
      visibleDeletions.push(
        ...pageDeletions.filter(
          ({ change }) => change.revision === pageRevisionId,
        ),
      )
    }

    return new VisibleRevisionChanges(
      visibleSpans,
      visibleDeletions,
      revisionMap,
    )
  }

  constructor(
    readonly spans: readonly Span[],
    private readonly ownedDeletions: readonly OwnedDeletionChange[],
    private readonly revisionMap: ReadonlyMap<string, Revision>,
  ) {}

  latestRevisionForLine(line: Line): Revision | null {
    const spans =
      line.from === line.to ? [] : this.spansBetween(line.from, line.to)
    return latestRevision(
      [
        ...spans,
        ...this.ownedDeletions
          .filter((owned) => owned.line === line)
          .map(({ change }) => change),
      ],
      this.revisionMap,
    )
  }

  private *spansBetween(from: number, to: number): Iterable<Span> {
    for (const span of this.spans) {
      if (span.from < to && span.to > from) {
        yield span
      }
    }
  }
}

function buildInsertionDecorations(
  newState: EditorState,
  tracker: RevisionTracker,
  revisionsState: RevisionsState,
): DecorationSet {
  const { revisionMap } = revisionsState

  const decorations: Decoration[] = []
  const pagination = getPagination(newState)
  const visibleChanges = revisionsState.visibleChanges({
    tracker,
    pagination,
    doc: newState.doc,
  })

  // use change data to create decorations for the revision text span
  for (const span of visibleChanges.spans) {
    const { from: start, to: end, revision } = span

    if (isInTitlePage(start, newState)) {
      continue
    }

    if (!revision) continue
    const rev = revisionMap.get(revision)
    if (!rev) continue

    decorations.push(
      Decoration.inline(
        start,
        end,
        {
          style: `color: ${rev.textColor}`,
          class: styles['revision-insertion'],
        },
        { key: revisionId(rev) },
      ),
    )
  }

  if (!pagination) {
    return DecorationSet.create(newState.doc, decorations)
  }

  // use change spans to create gutter decorations for changed lines.
  // If any change spansoverlap with a line, that line gets a gutter mark.
  // The type of mark depends on the revision set of the change span,
  // and the position depends on the right margin of the block (so that it shows up in the gutter).
  const { currentBlockFormats, currentPageConfig } = getConfigData(newState)
  const pageLayout = getPageLayout(currentPageConfig)

  const gutterBlocks: GutterBlockMark[] = []

  const addGutterBlock = (
    containerPos: number,
    lines: Line[],
    left: Unit,
    right: Unit,
  ) => {
    const revisionMarks = lines.map((line) => {
      const rev = visibleChanges.latestRevisionForLine(line)
      return rev ? rev.mark : ' '
    })

    if (revisionMarks.some((mark) => mark !== ' ')) {
      gutterBlocks.push({
        containerPos,
        marks: revisionMarks.map((ch) => ({ character: ch })),
        side: 'right',
        left,
        right,
        height: revisionMarks.length,
        pageLayout,
      })
    }
  }

  // a block can span multiple pages (e.g. a long dialogue continuation),
  // so it needs one gutter block per page it lands on, each anchored at
  // that page's first line of the block (not the block's own start pos)
  const { pages } = pagination
  let pageIndex = 0

  newState.doc.descendants((node, pos, parent) => {
    if (!parent?.type.isInGroup(NodeGroupMap.LAYOUT)) return

    const blockStartPos = pos + 1
    const blockEndPos = pos + node.nodeSize - 1

    const { left, right } = getBlockMargins(node, currentBlockFormats)

    // pages fully before this block will never be relevant again
    while (pageIndex < pages.length && pages[pageIndex].end <= blockStartPos) {
      pageIndex++
    }

    for (let i = pageIndex; i < pages.length; i++) {
      const pageInfo = pages[i]
      // pages are ordered by position; stop once we're past the block
      if (pageInfo.start >= blockEndPos) break

      const blockLines = pageInfo.lines.filter(
        (line) =>
          line.from >= blockStartPos &&
          line.to <= blockEndPos &&
          line.from >= pageInfo.start &&
          line.to <= pageInfo.end,
      )
      if (blockLines.length === 0) continue

      addGutterBlock(blockLines[0].from, blockLines, left, right)
    }

    return false
  })

  const insertDecorations = buildGutterDecorations(gutterBlocks, styles)
  decorations.push(...insertDecorations)

  return DecorationSet.create(newState.doc, decorations)
}

/**
 * Helper function to get the full revisions plugin state
 */
export function getRevisionsState(state: EditorState): RevisionsPluginState {
  return revisionsKey.getState(state) ?? INACTIVE_REVISIONS
}

export function getLoadedRevisionsState(
  state: EditorState,
): RevisionsState | undefined {
  const pluginState = getRevisionsState(state)
  if (pluginState.tag === 'Inactive') return undefined
  if (pluginState.tag === 'Error') return undefined
  return pluginState.revisionsState
}

export function areRevisionsEnabled(state: EditorState) {
  return getLoadedRevisionsState(state)?.enabled ?? false
}

/**
 * Resolves revision precedence for a document range (like a page)
 *
 * The most recent full draft establishes the minimum revision for the range. A
 * "higher" revision explicitly tracked in the range takes precedence -- higher
 * being the Final Draft terminology for a more recently applied revision set.
 *
 * @returns The highest applicable revision, or `undefined` when revisions are
 * unavailable or the range has no tracked revision or full-draft baseline
 */
export function highestRevisionForRange(
  state: EditorState,
  startPos: number,
  endPos: number,
): Revision | undefined {
  const pluginState = getRevisionsState(state)

  if (pluginState.tag !== 'Resolved') return undefined

  const { revisions, revisionMap } = pluginState.revisionsState
  const baselineRevision = revisions.mostRecentFullDraft
  const trackedRevision = latestRevision(
    [
      ...changesOverlappingNonEmptyTextBlocks(
        state.doc,
        pluginState.tracker.changesBetween(startPos, endPos),
        startPos,
        endPos,
      ),
      ...deletionsInRange(state, pluginState.tracker, startPos, endPos),
    ],
    revisionMap,
  )

  if (!trackedRevision) return baselineRevision
  if (!baselineRevision) return trackedRevision

  return trackedRevision.snapshot.version > baselineRevision.snapshot.version
    ? trackedRevision
    : baselineRevision
}

/**
 * A deletion is just a point in the doc, so it can land right on a page break.
 * When we have script pagination, it goes to whichever page shows its revision
 * mark. Otherwise we fall back to a plain range check, which counts a deletion
 * on the break toward both pages.
 */
function deletionsInRange(
  state: EditorState,
  tracker: RevisionTracker,
  startPos: number,
  endPos: number,
): readonly DeletionChange[] {
  const pagination = getPagination(state)
  if (!pagination) return tracker.deletions.between(startPos, endPos)

  return tracker.deletions
    .ownedBy(state.doc, pagination)
    .filter(({ line }) => line.from >= startPos && line.from < endPos)
    .map(({ change }) => change)
}

function changesOverlappingNonEmptyTextBlocks(
  doc: Node,
  spans: Span[],
  from: number,
  to: number,
): Span[] {
  const blockRanges: Array<{ from: number; to: number }> = []

  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isTextblock) return
    if (node.textContent.length > 0) {
      blockRanges.push({ from: pos, to: pos + node.nodeSize })
    }
    return false
  })

  const overlappingSpans: Span[] = []
  let spanIndex = 0
  let rangeIndex = 0

  // use two pointers to find overlapping spans and block ranges
  while (spanIndex < spans.length && rangeIndex < blockRanges.length) {
    const span = spans[spanIndex]
    const range = blockRanges[rangeIndex]

    if (span.to <= range.from) {
      spanIndex++
    } else if (range.to <= span.from) {
      rangeIndex++
    } else {
      overlappingSpans.push(span)
      spanIndex++
    }
  }

  return overlappingSpans
}

interface RevisionAttribution {
  revision: string | null
}

// Get the latest revision associated with a list of changes
export const latestRevision = (
  changes: Iterable<RevisionAttribution>,
  revisionMap: ReadonlyMap<string, Revision>,
): Revision | null => {
  let latestRevision: Revision | null = null

  for (const change of changes) {
    if (!change.revision) continue
    const rev = revisionMap.get(change.revision)
    if (!rev) continue
    if (
      !latestRevision ||
      revisionVersion(rev) > revisionVersion(latestRevision)
    ) {
      latestRevision = rev
    }
  }

  return latestRevision
}

export const includeInRevision: Command = (state, dispatch) => {
  const pluginState = getRevisionsState(state)
  if (pluginState.tag !== 'Resolved' || !pluginState.revisionsState.enabled) {
    return false
  }
  const { from, to } = state.selection
  if (from === to) return false
  const revision = revisionId(pluginState.revisionsState.revisions.active)
  const spans = pluginState.tracker.changesBetween(from, to)
  // split the selection into spans based on existing revision assignments,
  // so that we can assign the active revision to just the part of the selection that isn't already in that revision
  const splitSpans = splitRangeIntoSpans(from, to, spans)
  if (!splitSpans.length) return false

  const tr = state.tr
  splitSpans.forEach((span) => {
    if (span.revision === revision) return
    tr.step(RevisionStep.create(span.from, span.to, span.revision, revision))
  })
  closeHistory(tr)
  dispatch?.(tr)
  return true
}

export const excludeFromRevision: Command = (state, dispatch) => {
  const pluginState = getRevisionsState(state)
  if (pluginState.tag !== 'Resolved' || !pluginState.revisionsState.enabled) {
    return false
  }
  const { from, to } = state.selection
  if (from === to) return false
  const activeRevisionId = revisionId(
    pluginState.revisionsState.revisions.active,
  )
  // collect spans overlapping with the selection
  const spans = pluginState.tracker.changesBetween(from, to)
  const activeRevisions = spans.filter((s) => s.revision === activeRevisionId)
  if (!activeRevisions.length) return false

  const tr = state.tr
  // remove just the active revision assignment for the overlapping spans, keeping any other revisions intact
  activeRevisions.forEach((span) => {
    tr.step(RevisionStep.create(span.from, span.to, activeRevisionId, null))
  })
  closeHistory(tr)
  dispatch?.(tr)
  return true
}

// split a range into spans based on existing revision assignments,
// so that we can apply a new revision to just the part of the selection that isn't already in that revision
export function splitRangeIntoSpans(
  from: number,
  to: number,
  spans: Span[],
): Span[] {
  // create a flat list of all the span boundaries (start and end points), plus the overall from/to, and sort them
  const points = [
    ...new Set([
      from,
      to,
      ...spans.flatMap((s) => [s.from, s.to]).filter((p) => p > from && p < to),
    ]),
  ].sort((a, b) => a - b)

  // create spans between each pair of adjacent points, assigning the revision of the original spans that overlap with that range (if any)
  return points.slice(0, -1).map((spanFrom, i) => {
    const spanTo = points[i + 1]
    const revision =
      spans.find((s) => s.from < spanTo && s.to > spanFrom)?.revision ?? null
    return { from: spanFrom, to: spanTo, revision }
  })
}
